// Ported from project/assets/matters-data.js — one row per MATTER, indexed by eID.
// Cross-references clientId in clients.ts. Per-area variation lives only in
// matter.workspace; never branch in components.

import type { Matter } from "./types";
import type { PipelineStageCount } from "@duralex/shared";
import { CLIENTS } from "./clients";

export const MATTERS: Record<string, Matter> = {
  // CL-ANVAR - flagship multi-matter (asylum + U-visa)
  "DL-YJF4Q5": {
    eID: "DL-YJF4Q5",
    clientId: "cl-anvar",
    practiceArea: "immigration",
    subtype: "asylum",
    stage: "Master Hearing",
    opened: "2025-08-14",
    status: "active",
    needsAttention: true,
    primary: true,
    tags: [],
    driveFolder: "/Shared drives/Phillylawcenter/IMMIGRATION/KASYMOV, Anvar",
    matter: {
      type: "Immigration",
      subtype: "Defensive asylum",
      stage: "Master Hearing scheduled",
      receiptNumber: null,
      aNumber: "213-477-091",
      filedDate: "2025-09-30",
      venue: "Phila EOIR",
      derivativesCount: 3,
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-04-30",
        label: "Master Hearing — Phila EOIR Crtrm 3",
        bucket: "critical",
        days: 4,
      },
      {
        id: "d2",
        when: "2026-08-12",
        label: "I-94 expiry",
        bucket: "upcoming",
        days: 108,
      },
    ],
    documents: [
      {
        id: "doc-1",
        title: "Passport - Kasymov, Anvar",
        type: "passport",
        ext: "pdf",
        uploadedBy: "AI",
        date: "2026-04-25",
        aiConfidence: 0.99,
      },
      {
        id: "doc-2",
        title: "I-589 application v4",
        type: "i589",
        ext: "pdf",
        uploadedBy: "AI",
        date: "2026-04-22",
        aiConfidence: 0.96,
      },
      {
        id: "doc-3",
        title: "Personal declaration v4",
        type: "declaration",
        ext: "docx",
        uploadedBy: "DA",
        date: "2026-04-24",
      },
      {
        id: "doc-4",
        title: "Country conditions — Uzbekistan 2025",
        type: "country-conditions",
        ext: "pdf",
        uploadedBy: "FA",
        date: "2026-04-20",
      },
    ],
    aiSummary: {
      headline: "Master Hearing in 5 days — declaration revisions outstanding",
      confidence: 0.93,
      strengths: [
        "Strong corroborating evidence: HRW + State Dept reports cite same SVB facility",
        "Coherent timeline across passport, declaration, and HR-2018 protest photos",
      ],
      risks: [
        "DOB inconsistency between Uzbek passport and I-94 (one-day variance — likely transcription error, but flag it)",
        "Three declaration inconsistencies vs I-589 § 1.B (Darina flagged)",
      ],
      nextBestActions: [
        "Reconcile DOB before hearing — pull a contemporaneous record (school transcript)",
        "Sign-off declaration v5 by Apr 28",
      ],
    },
    missing: [
      { id: "m1", what: "School transcript with DOB", priority: "critical" },
      { id: "m2", what: "Husband's I-94", priority: "soon" },
    ],
    workspace: {
      i589: { sectionsComplete: 0.94, lastReviewedBy: "RB", flags: 3 },
      declaration: {
        version: "v4",
        words: 2840,
        signedOff: false,
        themes: ["political opinion", "religion (atheist)", "particular social group"],
      },
      coi: {
        sources: 14,
        latestDate: "2025-12-15",
        primaryReports: ["HRW 2024", "State Dept 2025"],
      },
      derivatives: [
        { name: "Husband (Bahodir Kasymov)", relationship: "spouse", age: 38 },
        { name: "Daughter (Sofia)", relationship: "child", age: 7 },
        { name: "Son (Nodir)", relationship: "child", age: 4 },
      ],
      eadClock: {
        i589FiledDate: "2025-09-30",
        day150Eligible: "2026-02-27",
        day180Eligible: "2026-03-29",
        currentDay: 209,
      },
    },
  },

  "DL-K2P9XM": {
    eID: "DL-K2P9XM",
    clientId: "cl-anvar",
    practiceArea: "immigration",
    subtype: "u-visa",
    stage: "Filed",
    opened: "2026-01-22",
    status: "active",
    needsAttention: false,
    primary: false,
    tags: [],
    driveFolder: "/Shared drives/Phillylawcenter/U-VISA/KASYMOV, Anvar",
    matter: {
      type: "Immigration",
      subtype: "U-visa (I-918)",
      stage: "Filed — awaiting bona fide determination",
      receiptNumber: "VSC2603012044",
      aNumber: "213-477-091",
      filedDate: "2026-02-15",
      venue: "VSC",
    },
    deadlines: [
      {
        id: "d1",
        when: "2027-02-15",
        label: "Bona fide expected (~12mo)",
        bucket: "upcoming",
        days: 295,
      },
    ],
    documents: [
      {
        id: "doc-1",
        title: "I-918 packet (filed)",
        type: "other",
        ext: "pdf",
        uploadedBy: "RB",
        date: "2026-02-15",
      },
      {
        id: "doc-2",
        title: "I-918 Supplement B (Phila PD)",
        type: "certification",
        ext: "pdf",
        uploadedBy: "RB",
        date: "2026-02-10",
      },
      {
        id: "doc-3",
        title: "Personal statement (cooperation)",
        type: "declaration",
        ext: "docx",
        uploadedBy: "DA",
        date: "2026-02-05",
      },
    ],
    aiSummary: {
      headline: "U-visa awaiting bona fide determination — strong cooperation record",
      confidence: 0.91,
      strengths: [
        "Phila PD I-918B certification — Det. Reyes signed",
        "Documented cooperation in 2024 robbery investigation",
      ],
      risks: [
        "Inadmissibility waiver (I-192) still pending — overlaps with asylum case if asylum denies",
      ],
      nextBestActions: [
        "Continue parallel asylum track in DL-YJF4Q5",
        "Bona fide expected ~Feb 2027",
      ],
    },
    missing: [],
    workspace: {
      i918: {
        partsComplete: 1.0,
        certifyingAgency: "Philadelphia PD",
        certifyingOfficer: "Det. M. Reyes",
        crime: "Robbery",
        crimeDate: "2024-08-12",
      },
      certification: { signed: true, signedDate: "2026-02-10", expires: "2026-08-10" },
      bonafide: {
        eligibilityDate: "2027-02-15",
        status: "pending",
        positionInQueue: "~14,500 of 280,000",
      },
    },
  },

  // CL-COLMENARES — asylum + divorce
  "DL-MC7K2Q": {
    eID: "DL-MC7K2Q",
    clientId: "cl-colmenares",
    practiceArea: "immigration",
    subtype: "asylum",
    stage: "Individual Hearing prep",
    opened: "2024-09-22",
    status: "active",
    needsAttention: false,
    primary: true,
    tags: [],
    driveFolder: "/Shared drives/Phillylawcenter/IMMIGRATION/COLMENARES, Maria",
    matter: {
      type: "Immigration",
      subtype: "Defensive asylum",
      stage: "Individual Hearing scheduled",
      aNumber: "212-988-441",
      filedDate: "2024-10-15",
      venue: "Phila EOIR",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-22",
        label: "Asylum interview — Newark Asylum Office",
        bucket: "thisWeek",
        days: 26,
      },
    ],
    documents: [],
    aiSummary: {
      headline: "Newark interview in ~4 weeks — declaration sign-off needed",
      confidence: 0.88,
      strengths: ["SEBIN detention documented — multi-source corroboration"],
      risks: ["Need to lock SEBIN facility name"],
      nextBestActions: [
        "Confirm SEBIN facility (request file from CasaSol)",
        "Schedule simulation #2",
      ],
    },
    missing: [{ id: "m1", what: "Confirm SEBIN facility name", priority: "soon" }],
    workspace: {
      i589: { sectionsComplete: 0.91 },
      declaration: {
        version: "v3",
        words: 2400,
        signedOff: false,
        themes: ["political opinion", "imputed political opinion"],
      },
      coi: { sources: 9, primaryReports: ["HRW 2023", "Amnesty 2024"] },
      derivatives: [{ name: "Spouse", relationship: "spouse", age: 41 }],
      eadClock: { i589FiledDate: "2024-10-15", currentDay: 559, eadIssued: "2025-04-13" },
    },
  },

  "DL-MC9R3T": {
    eID: "DL-MC9R3T",
    clientId: "cl-colmenares",
    practiceArea: "family",
    subtype: "divorce",
    stage: "Pleadings",
    opened: "2026-02-08",
    status: "active",
    needsAttention: true,
    primary: false,
    tags: [],
    driveFolder: "/Shared drives/Phillylawcenter/DIVORCES/COLMENARES, Maria",
    matter: {
      type: "Family Law",
      subtype: "Divorce (no-fault)",
      stage: "Complaint filed — answer pending",
      filedDate: "2026-02-15",
      venue: "Philadelphia Family Court",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-15",
        label: "Answer deadline (defendant)",
        bucket: "thisWeek",
        days: 19,
      },
    ],
    documents: [
      {
        id: "doc-1",
        title: "Divorce Complaint (filed)",
        type: "court-filing",
        ext: "pdf",
        uploadedBy: "RB",
        date: "2026-02-15",
      },
    ],
    aiSummary: {
      headline: "Defendant answer due in 19 days — no children, modest property",
      confidence: 0.84,
      strengths: ["No-fault, both parties willing", "No minor children"],
      risks: ["Spouse retained own counsel (J. Marlowe) — may seek alimony"],
      nextBestActions: ["Confirm equitable distribution worksheet", "Calendar answer deadline"],
    },
    missing: [],
    workspace: {
      parties: { plaintiff: "Maria Colmenares", defendant: "Roberto Colmenares" },
      grounds: "Irretrievable breakdown (no-fault)",
      children: 0,
      property: { jointAccount: "Wells Fargo (~$3,200)", residence: "Rented", retirement: null },
      support: { alimonyRequested: false, alimonyDemand: "TBD" },
    },
  },

  // CL-AKMATBEKOV — criminal + parallel removal defense
  "DL-AA8R4T": {
    eID: "DL-AA8R4T",
    clientId: "cl-akmatbekov",
    practiceArea: "criminal",
    subtype: "felony",
    stage: "Pre-trial",
    opened: "2025-10-19",
    status: "active",
    needsAttention: true,
    primary: true,
    tags: ["padilla-impact"],
    driveFolder: "/Shared drives/Phillylawcenter/CRIMINAL/Akmatbekov Adilet",
    matter: {
      type: "Criminal",
      subtype: "Felony — Aggravated Assault",
      stage: "Preliminary Hearing scheduled",
      filedDate: "2025-10-15",
      venue: "MDJ 07-1-07 Penndel",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-12",
        label: "Preliminary Hearing — MDJ 07-1-07",
        bucket: "thisWeek",
        days: 16,
      },
    ],
    documents: [],
    aiSummary: {
      headline: "Aggravated felony exposure — Padilla advisory + parallel removal defense required",
      confidence: 0.95,
      strengths: [
        "No aggravated factors as charged (no firearm, no SBI)",
        "Self-defense theory plausible w/ video",
      ],
      risks: [
        "Conviction = aggravated felony per 8 USC § 1101(a)(43)(F) — mandatory removal",
        "Discovery includes ER video — review urgent",
      ],
      nextBestActions: [
        "File suppression motion re: in-court ID",
        "Padilla advisory documented",
        "Coordinate with parallel removal defense (DL-AA2J5N)",
      ],
    },
    missing: [],
    workspace: {
      charges: [
        {
          count: 1,
          charge: "18 Pa.C.S. § 2702(a)(1) Aggravated Assault",
          grade: "F-1",
          maxJail: "20 years",
          maxFine: "$25,000",
          immigrationImpact: "AGGRAVATED FELONY",
        },
        {
          count: 2,
          charge: "18 Pa.C.S. § 2701 Simple Assault",
          grade: "M-2",
          maxJail: "2 years",
          maxFine: "$5,000",
          immigrationImpact: "CIMT",
        },
      ],
      defense: { theory: "Self-defense", strengthBars: { factual: 4, legal: 5 } },
      bail: { type: "Unsecured", amount: 25000, conditions: ["No contact w/ complainant"] },
      courtDates: [
        { date: "2026-05-12 13:30", event: "Preliminary Hearing", venue: "MDJ 07-1-07 Penndel" },
      ],
      immigrationImpact: {
        riskLevel: "HIGH",
        padillaAdvised: "2025-10-21",
        parallelRemovalCase: "DL-AA2J5N",
      },
    },
  },

  "DL-AA2J5N": {
    eID: "DL-AA2J5N",
    clientId: "cl-akmatbekov",
    practiceArea: "immigration",
    subtype: "removal-defense",
    stage: "Pending",
    opened: "2025-11-04",
    status: "active",
    needsAttention: false,
    primary: false,
    tags: ["padilla-impact"],
    driveFolder: "/Shared drives/Phillylawcenter/IMMIGRATION/Akmatbekov Adilet (parallel)",
    matter: {
      type: "Immigration",
      subtype: "Removal defense (parallel)",
      stage: "NTA issued — Master Hearing pending",
      aNumber: "215-893-022",
      venue: "Phila EOIR",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-08-04",
        label: "Master Hearing — Phila EOIR (post-criminal)",
        bucket: "upcoming",
        days: 100,
      },
    ],
    documents: [],
    aiSummary: {
      headline: "NTA issued — strategy hinges on outcome of parallel criminal case (DL-AA8R4T)",
      confidence: 0.78,
      strengths: ["Adjustment via USC parent possible if no aggravated felony"],
      risks: ["Aggravated felony conviction would bar most relief — see Padilla in DL-AA8R4T"],
      nextBestActions: [
        "Hold filing strategy until criminal disposition",
        "Prepare alternative relief packages (cancellation, asylum)",
      ],
    },
    missing: [],
    workspace: {
      i589: { applicable: false },
      relief: [
        "Adjustment of status (USC parent)",
        "Cancellation of removal (LPR)",
        "Asylum (backup)",
      ],
      parallelCriminalCase: "DL-AA8R4T",
    },
  },

  // Single-matter immigration clients
  "DL-T2VC4M": {
    eID: "DL-T2VC4M",
    clientId: "cl-kovalenko",
    practiceArea: "immigration",
    subtype: "asylum",
    stage: "Filed",
    opened: "2025-11-03",
    status: "active",
    needsAttention: true,
    tags: [],
    matter: {
      type: "Immigration",
      subtype: "Defensive asylum (UA)",
      stage: "I-589 drafted, signature pending",
      aNumber: "215-771-066",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-04-30",
        label: "1-year filing deadline",
        bucket: "critical",
        days: 4,
      },
    ],
    aiSummary: {
      headline:
        "I-589 one-year filing deadline closes Apr 30 — drafted by AI, awaiting Robert's signature",
      confidence: 0.94,
      strengths: [],
      risks: ["Cutoff in 4 days"],
      nextBestActions: ["Robert sign-off"],
    },
    workspace: {
      i589: { sectionsComplete: 0.97 },
      declaration: { version: "v2", words: 1900, signedOff: false },
    },
  },

  "DL-FH3JQ8": {
    eID: "DL-FH3JQ8",
    clientId: "cl-mendez",
    practiceArea: "immigration",
    subtype: "family-petition",
    stage: "Pending",
    opened: "2024-06-12",
    status: "active",
    needsAttention: true,
    tags: [],
    matter: {
      type: "Immigration",
      subtype: "I-130 (Family-based)",
      stage: "Pending — biometrics window",
      receiptNumber: "PHI2526031044",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-03",
        label: "I-693 medical exam expires",
        bucket: "thisWeek",
        days: 7,
      },
    ],
    aiSummary: {
      headline: "I-693 medical expires May 3 — biometrics window narrowing",
      confidence: 0.92,
      strengths: [],
      risks: ["Medical expiry"],
      nextBestActions: ["Schedule new I-693 if needed"],
    },
    workspace: {
      i130: {
        petitioner: "USC spouse",
        beneficiary: "Carlos Mendez (MX)",
        priorityDate: "2024-08-15",
      },
    },
  },

  "DL-Z7WB6V": {
    eID: "DL-Z7WB6V",
    clientId: "cl-zhang",
    practiceArea: "immigration",
    subtype: "naturalization",
    stage: "Ready to file",
    opened: "2024-04-08",
    status: "active",
    needsAttention: true,
    tags: [],
    matter: {
      type: "Immigration",
      subtype: "N-400 Naturalization",
      stage: "Cover letter & N-400 packet drafted (96% conf), Robert's review pending",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-04-26",
        label: "Robert's signature today",
        bucket: "critical",
        days: 0,
      },
    ],
    aiSummary: {
      headline: "N-400 packet AI-drafted and ready for Robert's signature",
      confidence: 0.96,
      strengths: ["5-yr LPR clock cleanly met", "No GMC issues"],
      risks: [],
      nextBestActions: ["Sign and file"],
    },
    workspace: {
      n400: {
        lprDate: "2020-07-12",
        physicalPresenceDays: 1820,
        gmcIssues: 0,
        civicsTestReady: true,
      },
    },
  },

  "DL-MW5N9X": {
    eID: "DL-MW5N9X",
    clientId: "cl-petrov",
    practiceArea: "immigration",
    subtype: "removal-defense",
    stage: "Pending",
    opened: "2024-12-01",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: {
      type: "Immigration",
      subtype: "Cancellation of Removal",
      stage: "DOB conflict flagged — human review needed",
      aNumber: "214-552-018",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-06",
        label: "Reconcile DOB before Master Hearing",
        bucket: "thisWeek",
        days: 10,
      },
    ],
    aiSummary: {
      headline: "DOB conflict between passport and I-862 — reconcile before Master",
      confidence: 0.62,
      strengths: [],
      risks: ["Identity inconsistency"],
      nextBestActions: ["Pull contemporaneous record"],
    },
    workspace: {
      cancellation: {
        yearsLPR: 12,
        gmc: "clean",
        removable: "false-statement (8 USC § 1227)",
        relief: "Cancellation 240A(a)",
      },
    },
  },

  "DL-K8RT2P": {
    eID: "DL-K8RT2P",
    clientId: "cl-garcia",
    practiceArea: "immigration",
    subtype: "asylum",
    stage: "Master Hearing",
    opened: "2024-08-19",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: {
      type: "Immigration",
      subtype: "Defensive asylum",
      stage: "Master Hearing prep — supplemental evidence packet ready",
      aNumber: "215-110-893",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-01 14:30",
        label: "Master Hearing — Phila EOIR",
        bucket: "thisWeek",
        days: 5,
      },
    ],
    aiSummary: {
      headline: "Supplemental evidence packet AI-organized — 12 docs",
      confidence: 0.93,
      strengths: [],
      risks: [],
      nextBestActions: ["Final packet review"],
    },
    workspace: { i589: { sectionsComplete: 0.99 }, supplementalCount: 12 },
  },

  // SSI / SSDI
  "DL-RS6X9P": {
    eID: "DL-RS6X9P",
    clientId: "cl-rsmith",
    practiceArea: "ssi",
    subtype: undefined,
    stage: "ALJ hearing",
    opened: "2025-02-14",
    status: "active",
    needsAttention: true,
    tags: [],
    matter: {
      type: "SSI/SSDI",
      subtype: "ALJ disability hearing",
      stage: "ALJ hearing scheduled",
      filedDate: "2024-12-08",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-19",
        label: "ALJ hearing — Phila ODAR Rm 2",
        bucket: "thisWeek",
        days: 23,
      },
      {
        id: "d2",
        when: "2026-05-12",
        label: "Pre-hearing brief due to ALJ",
        bucket: "thisWeek",
        days: 16,
      },
    ],
    aiSummary: {
      headline:
        "ALJ hearing in 23 days — pre-hearing brief due in 16, vocational expert prep needed",
      confidence: 0.86,
      strengths: ["Treating physician's RFC <= sedentary"],
      risks: ["VE may identify alternate jobs"],
      nextBestActions: ["Draft pre-hearing brief", "Schedule simulation w/ client"],
    },
    workspace: {
      claim: {
        onsetDate: "2023-11-15",
        insuredStatus: "DLI 2026-12-31",
        primaryDx: "Lumbar radiculopathy + major depression",
      },
      providers: ["Dr. K. Patel (PCP)", "Dr. M. Liu (orthopedic)", "Dr. A. Reyes (psychiatrist)"],
      rfc: { exertional: "sedentary", postural: "limited", mental: "marked limitation in CPP" },
      pastWork: "Warehouse picker (medium, 12 yrs)",
      veRebuttal: { hypothetical: "sedentary + 1 hour absence per week", erodedJobs: 0.85 },
    },
  },

  // WILLS
  "DL-BL3M7K": {
    eID: "DL-BL3M7K",
    clientId: "cl-bealick",
    practiceArea: "wills",
    subtype: "will",
    stage: "Drafting",
    opened: "2026-01-08",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: {
      type: "Wills & Estates",
      subtype: "Will + POA Financial + POA Medical + Living Will",
      stage: "Drafting — review pending",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-08",
        label: "Notarization signing appointment",
        bucket: "thisWeek",
        days: 12,
      },
    ],
    aiSummary: {
      headline: "Four-pack drafted — notarization booked May 8",
      confidence: 0.94,
      strengths: ["Simple estate, clear beneficiaries"],
      risks: [],
      nextBestActions: ["Final review with client", "Confirm notary appointment"],
    },
    workspace: {
      docPackage: ["Will", "POA Financial", "POA Medical (HCD)", "Living Will"],
      beneficiaries: [
        { name: "Daughter (Olga Bealick)", share: 0.7 },
        { name: "Son (Mikhail Bealick)", share: 0.3 },
      ],
      executor: "Daughter (Olga Bealick)",
      assets: {
        residence: "Owned (Northeast Phila)",
        retirement: "$240k IRA",
        lifeInsurance: "$100k",
      },
      witnesses: 2,
    },
  },

  // MUNICIPAL
  "DL-EA8K2N": {
    eID: "DL-EA8K2N",
    clientId: "cl-aleixo",
    practiceArea: "municipal",
    subtype: "landlord-tenant",
    stage: "Hearing",
    opened: "2026-02-20",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: {
      type: "Municipal Court",
      subtype: "Landlord-Tenant — eviction defense",
      stage: "Hearing scheduled",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-14 09:00",
        label: "L-T hearing — Phila MC Rm 6",
        bucket: "thisWeek",
        days: 18,
      },
    ],
    aiSummary: {
      headline: "Eviction defense — habitability counterclaim plausible",
      confidence: 0.81,
      strengths: ["Documented 311 complaints (mold, heat)", "Rent escrow possible"],
      risks: ["Lease terminated for material breach if uncured"],
      nextBestActions: ["File rent-escrow motion", "Photograph current conditions"],
    },
    workspace: {
      lease: { startDate: "2023-05-01", monthlyRent: 1450, escrowEligible: true },
      complaints311: 4,
      photos: 28,
    },
  },

  // PI
  "DL-KA9Y3F": {
    eID: "DL-KA9Y3F",
    clientId: "cl-ahmedov",
    practiceArea: "pi",
    subtype: "mva",
    stage: "Treatment",
    opened: "2024-09-15",
    status: "active",
    needsAttention: false,
    tags: ["drew-referral"],
    matter: {
      type: "Personal Injury",
      subtype: "MVA — DOA 09.10.18",
      stage: "Active treatment, demand drafting",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-09-10",
        label: "PA SOL (2 yrs from accident)",
        bucket: "upcoming",
        days: 137,
      },
    ],
    aiSummary: {
      headline: "Active treatment ongoing — demand letter draft underway",
      confidence: 0.85,
      strengths: ["Clear liability (rear-end)", "Police report secured"],
      risks: ["MIST defense possible if treatment stalls"],
      nextBestActions: ["Lock MMI date", "Compile medical specials"],
    },
    workspace: {
      liability: "Defendant 100%",
      treatment: { providers: 3, sessions: 22, MMI: false },
      specials: { medical: 18400, lostWages: 4800 },
      demand: { draftStatus: "in-progress" },
    },
  },

  // TRAFFIC
  "DL-DN4T8H": {
    eID: "DL-DN4T8H",
    clientId: "cl-norimov",
    practiceArea: "traffic",
    subtype: "moving-violation",
    stage: "Pre-trial",
    opened: "2026-04-15",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: {
      type: "Traffic",
      subtype: "Speeding 75 in 55 + reckless",
      stage: "Pre-trial 06.10.26",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-06-10 09:00",
        label: "Pre-trial hearing — MDJ Bensalem",
        bucket: "upcoming",
        days: 45,
      },
    ],
    aiSummary: {
      headline: "Pre-trial in ~6 weeks — CDL implications",
      confidence: 0.79,
      strengths: ["No prior CDL violations within 3 yrs"],
      risks: ["Reckless reduces to careless = saves CDL"],
      nextBestActions: ["Mitigation letter w/ traffic safety course"],
    },
    workspace: {
      points: { current: 3, ifConvicted: 6 },
      cdl: true,
      license: "PA CDL Class A",
      priorWithin3yr: 0,
    },
  },

  // DUI
  "DL-SD2W6V": {
    eID: "DL-SD2W6V",
    clientId: "cl-divgun",
    practiceArea: "dui",
    subtype: "dui-1st",
    stage: "Pre-trial",
    opened: "2026-02-14",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: { type: "DUI", subtype: "DUI 1st — high BAC", stage: "ARD application pending" },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-21",
        label: "ARD eval interview",
        bucket: "thisWeek",
        days: 25,
      },
    ],
    aiSummary: {
      headline: "DUI 1st with high BAC — ARD likely if eval clean",
      confidence: 0.84,
      strengths: ["First offense, no aggravators"],
      risks: ["BAC 0.18 puts in highest tier — 12-18 months license"],
      nextBestActions: ["ARD application w/ AA documentation", "Ignition interlock on day 1"],
    },
    workspace: {
      bac: 0.18,
      refusal: false,
      priorDUI: 0,
      ARDEligible: true,
      licenseSuspension: "12 months (tier 3)",
    },
  },

  // MED MAL
  "DL-YB2V8K": {
    eID: "DL-YB2V8K",
    clientId: "cl-belovol",
    practiceArea: "medmal",
    subtype: "birth-injury",
    stage: "Records review",
    opened: "2025-12-04",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: {
      type: "Med Mal",
      subtype: "Birth injury — shoulder dystocia",
      stage: "Records secured, expert engagement",
    },
    deadlines: [
      {
        id: "d1",
        when: "2027-04-15",
        label: "PA SOR (7 yr — minor's injury)",
        bucket: "upcoming",
        days: 354,
      },
    ],
    aiSummary: {
      headline: "Records under expert review — Certificate of Merit timeline tight",
      confidence: 0.72,
      strengths: ["Documented Erb's palsy"],
      risks: ["Proximate cause contested"],
      nextBestActions: ["Lock pediatric neurology expert", "Pre-suit notice"],
    },
    workspace: {
      damages: { economic: "TBD", noneconomic: "TBD" },
      experts: { sought: 2, retained: 0 },
      certOfMerit: { drafted: false },
    },
  },

  // CIVIL
  "DL-SA7B4M": {
    eID: "DL-SA7B4M",
    clientId: "cl-abele",
    practiceArea: "civil",
    subtype: "consumer-protection",
    stage: "Discovery",
    opened: "2025-06-10",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: { type: "Civil", subtype: "Plaintiff — vs. Citizens Bank", stage: "Discovery pending" },
    deadlines: [
      {
        id: "d1",
        when: "2026-07-08",
        label: "Discovery cut-off",
        bucket: "upcoming",
        days: 73,
      },
    ],
    aiSummary: {
      headline: "Discovery — pending depositions of bank officers",
      confidence: 0.78,
      strengths: ["Documented unauthorized fees pattern"],
      risks: ["Class certification denied earlier — proceeding individually"],
      nextBestActions: ["Notice depositions", "Draft 30(b)(6) topics"],
    },
    workspace: {
      claims: ["UDAP (PA Unfair Trade Practices)", "Breach of contract"],
      damages: { actual: 28400, treble: 85200 },
    },
  },

  // FEDERAL APPEALS — Sobel of-counsel
  "DL-MS3X9P": {
    eID: "DL-MS3X9P",
    clientId: "cl-sobel-ref-001",
    practiceArea: "appeals",
    subtype: "habeas-appeal",
    stage: "Opening brief",
    opened: "2026-02-24",
    status: "active",
    needsAttention: true,
    tags: ["sobel-of-counsel", "habeas"],
    matter: {
      type: "Federal Appeals",
      subtype: "Third Circuit § 1291 (habeas appeal)",
      stage: "Opening brief",
      venue: "3d Cir.",
    },
    deadlines: [
      {
        id: "d1",
        when: "2026-05-26",
        label: "Opening Brief due (3d Cir.)",
        bucket: "thisWeek",
        days: 30,
      },
    ],
    aiSummary: {
      headline:
        "Third Circuit opening brief due in 30 days — § 1226(e) jurisdictional issue central",
      confidence: 0.83,
      strengths: ["German Santos analog", "Constitutional procedural-due-process challenge"],
      risks: ["§ 1226(e) jurisdictional bar"],
      nextBestActions: ["Lock § 1226(e) navigation per skill", "Midpage citation pass"],
    },
    workspace: {
      issuesPresented: 3,
      wordCount: 0,
      wordLimit: 13000,
      midpageVerified: false,
      citationCount: 0,
    },
  },

  // BANKRUPTCY
  "DL-RB-001": {
    eID: "DL-RB-001",
    clientId: "cl-rsmith",
    practiceArea: "bankruptcy",
    subtype: "chapter-7",
    stage: "Means test",
    opened: "2026-04-02",
    status: "active",
    needsAttention: false,
    tags: [],
    matter: { type: "Bankruptcy", subtype: "Chapter 7", stage: "Means test in progress" },
    deadlines: [
      {
        id: "d1",
        when: "2026-06-15",
        label: "Petition filing target",
        bucket: "upcoming",
        days: 50,
      },
    ],
    aiSummary: {
      headline: "Means test in progress — likely Chapter 7 eligible",
      confidence: 0.7,
      strengths: [],
      risks: [],
      nextBestActions: ["Complete means test", "Compile schedules A-J"],
    },
    workspace: {
      meansTest: { complete: false, eligible: null },
      schedules: { drafted: 0, total: 10 },
    },
  },
};

// =====================================================================
// SELECTORS
// =====================================================================

export const matterById = (eID: string): Matter | null => MATTERS[eID] ?? null;
export const matterList = (): Matter[] => Object.values(MATTERS);
export const mattersForClient = (clientId: string): Matter[] =>
  matterList().filter((m) => m.clientId === clientId);
export const matterClient = (matter: Matter | null | undefined) =>
  matter && CLIENTS[matter.clientId] ? CLIENTS[matter.clientId] : null;
export const mattersByPracticeArea = (paId: string): Matter[] =>
  matterList().filter((m) => m.practiceArea === paId);
export const mattersByTag = (tag: string): Matter[] =>
  matterList().filter((m) => (m.tags || []).includes(tag));

export interface FirmCounts {
  activeMatters: number;
  clients: number;
  unmatched: number;
  needsReview: number;
  needsAttention: number;
  readyToFile: number;
  deadlinesCritical: number;
  deadlinesThisWeek: number;
  deadlinesOverdue: number;
  byPracticeArea: Record<string, number>;
  /** Keyed like byPracticeArea; absent while the API predates it. */
  pipelineByArea?: Record<string, PipelineStageCount[]>;
}
